import React, { useState } from 'react';
import { Card, Button, Stack, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ShieldLock, ShieldCheck, ShieldShaded, PencilSquare, ExclamationTriangle, Trash3, ShieldExclamation } from 'react-bootstrap-icons';
import ChangePassword from './ChangePassword';
import paths from 'routes/paths';

const UserRoles = () => {
    const navigate = useNavigate();
    const { userData } = useSelector((state) => state.auth);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);

    const roles = userData?.roles || [
        { id: 2, name: 'Eligibility', color: 'primary' },
        { id: 3, name: 'Preauthorization', color: 'success' },
        { id: 4, name: 'Claims', color: 'info' }
    ];

    const handleEditRoles = () => {
        // Redirect to the first role's edit permission page, or a default id if none
        const primaryRole = roles[0];
        const roleId = primaryRole?.id || 2;
        navigate(`${paths.editPermission}/${roleId}`);
    };

    return (
        <>
            <Card className="border-0 shadow-sm rounded-3">
                <Card.Body className="p-4">
                    <div className="d-flex align-items-center mb-4 gap-2">
                        <div className="bg-primary-subtle p-2 rounded-2 d-flex align-items-center justify-content-center">
                            <ShieldLock className="text-primary" size={18} />
                        </div>
                        <h6 className="fw-bold mb-0 text-primary fs-10">Security Settings</h6>
                    </div>

                    {/* Password Section */}
                    <ChangePassword />

                    {/* Two-Factor Authentication Section */}
                    <div className="p-3 mb-4 rounded-3 d-flex align-items-center justify-content-between border border-translucent">
                        <div className="d-flex align-items-center">
                            <div className="p-2 rounded-2 shadow-sm me-3">
                                <ShieldShaded className="text-success" size={20} />
                            </div>
                            <div>
                                <h6 className="mb-0 fs-11 fw-bold text-900">Two-Factor Authentication</h6>
                                <p className="mb-0 fs-11 text-500">Protects your account with an extra layer of security</p>
                            </div>
                        </div>
                        <Button variant="success" className="btn-sm border-0 fs-11 fw-bold px-3 py-1 text-white">
                            Enabled
                        </Button>
                    </div>

                    <hr className="border-300 my-4" />

                    {/* Roles Section */}
                    <div className="mb-4">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <div className="bg-primary-subtle p-2 rounded-2 d-flex align-items-center justify-content-center">
                                    <ShieldCheck className="text-primary" size={18} />
                                </div>
                                <h6 className="fw-bold mb-0 text-primary fs-10">Assigned Roles</h6>
                            </div>
                            <Button
                                variant="link"
                                className="p-0 text-primary text-decoration-none fs-11 fw-bold d-flex align-items-center gap-1"
                                onClick={handleEditRoles}
                            >
                                <PencilSquare size={14} />
                                Edit Roles
                            </Button>
                        </div>
                        <Stack direction="horizontal" gap={2} className="flex-wrap ps-1">
                            {roles.map((role, index) => (
                                <div key={index} className={`d-flex align-items-center bg-${role.color || 'primary'}-subtle border border-${role.color || 'primary'}-subtle px-3 py-1 rounded-pill`}>
                                    <div className={`bg-${role.color || 'primary'} rounded-circle me-2`} style={{ width: '8px', height: '8px' }}></div>
                                    <span className={`text-${role.color || 'primary'} fw-bold fs-11`}>{role.name || role}</span>
                                </div>
                            ))}
                        </Stack>
                    </div>

                    <hr className="border-300 my-4" />

                    {/* Danger Zone Section */}
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <ExclamationTriangle className="text-danger" size={16} />
                            <h6 className="fw-bold mb-0 text-danger fs-11">Danger Zone</h6>
                        </div>
                        <p className="text-500 fs-11 mb-3">Irreversible actions concerning your account.</p>
                        <Button
                            variant="outline-danger"
                            className="btn-sm px-4 py-2 fs-11 fw-bold text-uppercase"
                            onClick={() => setShowDeactivateModal(true)}
                        >
                            Deactivate Account
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* Deactivation Confirmation Modal */}
            <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered contentClassName="border-0 shadow-lg">
                <Modal.Body className="p-0 overflow-hidden rounded-3">
                    <div className="bg-danger p-4 text-center">
                        <div className="bg-white d-inline-flex p-3 rounded-circle mb-2 shadow-sm">
                            <ExclamationTriangle className="text-danger" size={32} />
                        </div>
                        <h5 className="fw-bold text-white mb-0">Extreme Caution Required!</h5>
                    </div>

                    <div className="p-4 p-md-5">
                        <h6 className="fw-bold text-900 mb-3 fs-9">Are you absolutely sure you want to deactivate your account?</h6>
                        <p className="text-600 fs-11 mb-4">
                            Deactivating your account is a serious action. You will lose access to all system features, active claims, and administrative privileges immediately. This action cannot be undone by yourself.
                        </p>

                        <div className="bg-light p-3 rounded-3 mb-4 border border-translucent">
                            <ul className="list-unstyled mb-0 fs-11 text-600">
                                <li className="mb-2 d-flex align-items-center gap-2">
                                    <div className="bg-danger rounded-circle" style={{ width: '6px', height: '6px', flexShrink: 0 }}></div>
                                    <span className="lh-1">Instant logout and session termination.</span>
                                </li>
                                <li className="mb-2 d-flex align-items-center gap-2">
                                    <div className="bg-danger rounded-circle" style={{ width: '6px', height: '6px', flexShrink: 0 }}></div>
                                    <span className="lh-1">Removal of all active permissions and roles.</span>
                                </li>
                                <li className="d-flex align-items-center gap-2">
                                    <div className="bg-danger rounded-circle" style={{ width: '6px', height: '6px', flexShrink: 0 }}></div>
                                    <span className="lh-1">Account status marked as <strong>Inactive</strong>.</span>
                                </li>
                            </ul>
                        </div>

                        <div className="d-flex gap-3">
                            <Button
                                variant="light"
                                className="flex-fill py-2 fw-bold fs-11 text-700 border border-300 shadow-none"
                                onClick={() => setShowDeactivateModal(false)}
                            >
                                Cancel, I'll stay
                            </Button>
                            <Button
                                variant="danger"
                                className="flex-fill py-2 fw-bold fs-11 shadow-sm d-flex align-items-center justify-content-center gap-2"
                                onClick={() => {
                                    // Deactivation logic would go here
                                    setShowDeactivateModal(false);
                                }}
                            >
                                <Trash3 size={14} />
                                Deactivate Now
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            <style dangerouslySetInnerHTML={{
                __html: `
                .border-translucent { border-color: rgba(0, 0, 0, 0.05) !important; }
                .btn-white { background-color: #fff; border-color: #d8e2ef; color: #5e6e82; }
                .btn-white:hover { background-color: #f9fafd; border-color: #b6c1d2; }
                .letter-spacing-1 { letter-spacing: 0.05em; }
            `}} />
        </>
    );
};

export default UserRoles;
